import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface AdminRestaurant {
  id: string;
  name: string;
  slug: string;
  phone: string | null;
  address: string | null;
  is_active: boolean | null;
  created_at: string;
  subscription?: {
    id: string;
    status: string;
    plan: string;
    amount: number;
    expires_at: string | null;
    suspended_at: string | null;
    suspended_reason: string | null;
  } | null;
  owner?: {
    user_id: string;
    full_name: string | null;
    phone: string | null;
  } | null;
  last_login?: string | null;
  last_order?: string | null;
}

export interface AdminUser {
  id: string;
  user_id: string;
  role: string;
  created_at: string;
  profile: {
    full_name: string | null;
    phone: string | null;
  } | null;
  restaurants: {
    id: string;
    name: string;
  }[];
}

export interface SubscriptionPayment {
  id: string;
  subscription_id: string;
  restaurant_id: string;
  amount: number;
  payment_date: string;
  reference_month: number;
  reference_year: number;
  status: string;
  payment_method: string | null;
  notes: string | null;
  restaurant?: {
    name: string;
  };
}

export function useAdminData() {
  const [restaurants, setRestaurants] = useState<AdminRestaurant[]>([]);
  const [payments, setPayments] = useState<SubscriptionPayment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    totalRestaurants: 0,
    activeRestaurants: 0,
    suspendedRestaurants: 0,
    totalRevenue: 0,
    pendingPayments: 0,
  });

  const fetchRestaurants = useCallback(async () => {
    try {
      // Buscar restaurantes
      const { data: restaurantsData, error: restaurantsError } = await supabase
        .from('restaurants')
        .select('*')
        .order('created_at', { ascending: false });

      if (restaurantsError) throw restaurantsError;

      // Buscar assinaturas
      const { data: subscriptionsData, error: subscriptionsError } = await supabase
        .from('restaurant_subscriptions')
        .select('*');

      if (subscriptionsError) throw subscriptionsError;

      // Buscar owners de cada restaurante
      const { data: ownersData, error: ownersError } = await supabase
        .from('restaurant_users')
        .select(`
          restaurant_id,
          user_id,
          profile:profiles(full_name, phone)
        `)
        .eq('role', 'restaurant_owner');

      if (ownersError) throw ownersError;

      // Último login / último pedido (via edge function, precisa de service role)
      let activity: Record<string, { last_login: string | null; last_order: string | null }> = {};
      try {
        const { data: activityData, error: activityError } = await supabase.functions.invoke(
          'restaurant-activity'
        );
        if (activityError) throw activityError;
        activity = (activityData as any)?.activity || {};
      } catch (err) {
        console.warn('[useAdminData] Não foi possível carregar atividade dos restaurantes:', err);
      }

      // Combinar dados
      const combined = restaurantsData.map(restaurant => {
        const subscription = subscriptionsData?.find(s => s.restaurant_id === restaurant.id);
        const ownerData = ownersData?.find(o => o.restaurant_id === restaurant.id);
        const act = activity[restaurant.id];

        return {
          ...restaurant,
          subscription: subscription || null,
          owner: ownerData ? {
            user_id: ownerData.user_id,
            full_name: (ownerData.profile as any)?.full_name || null,
            phone: (ownerData.profile as any)?.phone || null,
          } : null,
          last_login: act?.last_login ?? null,
          last_order: act?.last_order ?? null,
        };
      });

      setRestaurants(combined);

      // Calcular estatísticas
      const active = combined.filter(r => !r.subscription || r.subscription.status === 'active' || r.subscription.status === 'trial').length;
      const suspended = combined.filter(r => r.subscription?.status === 'suspended').length;

      setStats(prev => ({
        ...prev,
        totalRestaurants: combined.length,
        activeRestaurants: active,
        suspendedRestaurants: suspended,
      }));
    } catch (error) {
      console.error('Error fetching restaurants:', error);
      toast.error('Erro ao carregar restaurantes');
    }
  }, []);

  const fetchPayments = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('subscription_payments')
        .select(`
          *,
          restaurant:restaurants(name)
        `)
        .order('payment_date', { ascending: false })
        .limit(100);

      if (error) throw error;

      setPayments(data || []);

      // Calcular receita total e pendentes
      const totalRevenue = data
        ?.filter(p => p.status === 'paid')
        .reduce((sum, p) => sum + Number(p.amount), 0) || 0;
      
      const pendingPayments = data?.filter(p => p.status === 'pending').length || 0;

      setStats(prev => ({
        ...prev,
        totalRevenue,
        pendingPayments,
      }));
    } catch (error) {
      console.error('Error fetching payments:', error);
      toast.error('Erro ao carregar pagamentos');
    }
  }, []);

  const suspendRestaurant = async (restaurantId: string, reason: string) => {
    try {
      // Verificar se já existe assinatura
      const { data: existing } = await supabase
        .from('restaurant_subscriptions')
        .select('id')
        .eq('restaurant_id', restaurantId)
        .maybeSingle();

      if (existing) {
        // Atualizar assinatura existente
        const { error } = await supabase
          .from('restaurant_subscriptions')
          .update({
            status: 'suspended',
            suspended_at: new Date().toISOString(),
            suspended_reason: reason,
          })
          .eq('restaurant_id', restaurantId);

        if (error) throw error;
      } else {
        // Criar assinatura com status suspenso
        const { error } = await supabase
          .from('restaurant_subscriptions')
          .insert({
            restaurant_id: restaurantId,
            plan: 'basic',
            amount: 0,
            status: 'suspended',
            suspended_at: new Date().toISOString(),
            suspended_reason: reason,
          });

        if (error) throw error;
      }

      // Também desativar o restaurante
      await supabase
        .from('restaurants')
        .update({ is_active: false })
        .eq('id', restaurantId);

      toast.success('Restaurante suspenso');
      fetchRestaurants();
    } catch (error) {
      console.error('Error suspending restaurant:', error);
      toast.error('Erro ao suspender restaurante');
    }
  };

  const activateRestaurant = async (restaurantId: string) => {
    try {
      const { error } = await supabase
        .from('restaurant_subscriptions')
        .update({
          status: 'active',
          suspended_at: null,
          suspended_reason: null,
        })
        .eq('restaurant_id', restaurantId);

      if (error) throw error;

      // Também reativar o restaurante
      await supabase
        .from('restaurants')
        .update({ is_active: true })
        .eq('id', restaurantId);

      toast.success('Restaurante ativado');
      fetchRestaurants();
    } catch (error) {
      console.error('Error activating restaurant:', error);
      toast.error('Erro ao ativar restaurante');
    }
  };

  const createSubscription = async (restaurantId: string, plan: string, amount: number) => {
    try {
      const { error } = await supabase
        .from('restaurant_subscriptions')
        .insert({
          restaurant_id: restaurantId,
          plan,
          amount,
          status: 'active',
        });

      if (error) throw error;

      toast.success('Assinatura criada');
      fetchRestaurants();
    } catch (error) {
      console.error('Error creating subscription:', error);
      toast.error('Erro ao criar assinatura');
    }
  };

  const registerPayment = async (
    subscriptionId: string,
    restaurantId: string,
    amount: number,
    referenceMonth: number,
    referenceYear: number,
    paymentMethod: string,
    registeredBy: string
  ) => {
    try {
      const { error } = await supabase
        .from('subscription_payments')
        .insert({
          subscription_id: subscriptionId,
          restaurant_id: restaurantId,
          amount,
          reference_month: referenceMonth,
          reference_year: referenceYear,
          payment_method: paymentMethod,
          status: 'paid',
          registered_by: registeredBy,
        });

      if (error) throw error;

      toast.success('Pagamento registrado');
      fetchPayments();
    } catch (error) {
      console.error('Error registering payment:', error);
      toast.error('Erro ao registrar pagamento');
    }
  };

  const createRestaurant = async (data: {
    name: string;
    slug: string;
    phone?: string;
    address?: string;
    plan?: string;
    amount?: number;
    createOwner?: boolean;
    ownerEmail?: string;
    ownerPassword?: string;
    ownerName?: string;
    ownerPhone?: string;
    sendWelcomeEmail?: boolean;
  }) => {
    try {
      // Criar restaurante
      const { data: restaurant, error: restaurantError } = await supabase
        .from('restaurants')
        .insert({
          name: data.name,
          slug: data.slug,
          phone: data.phone || null,
          address: data.address || null,
        })
        .select()
        .single();

      if (restaurantError) throw restaurantError;

      // Criar assinatura se informado plano
      if (data.plan) {
        await supabase
          .from('restaurant_subscriptions')
          .insert({
            restaurant_id: restaurant.id,
            plan: data.plan,
            amount: data.amount || 0,
            status: data.plan === 'trial' ? 'trial' : 'active',
          });
      }

      // Criar usuário proprietário se solicitado
      if (data.createOwner && data.ownerEmail && data.ownerPassword) {
        const { data: result, error: fnError } = await supabase.functions.invoke('create-user', {
          body: {
            email: data.ownerEmail,
            password: data.ownerPassword,
            fullName: data.ownerName,
            phone: data.ownerPhone,
            restaurantId: restaurant.id,
            sendWelcomeEmail: data.sendWelcomeEmail ?? false,
            restaurantName: data.name,
          },
        });
        
        if (fnError) {
          toast.error(`Restaurante criado, mas erro ao criar usuário: ${fnError.message}`);
        } else {
          toast.success('Restaurante e usuário criados com sucesso');
        }
      } else {
        toast.success('Restaurante criado com sucesso');
      }

      fetchRestaurants();
      return restaurant;
    } catch (error: any) {
      console.error('Error creating restaurant:', error);
      if (error.code === '23505') {
        toast.error('Slug já existe. Escolha outro identificador.');
      } else {
        toast.error('Erro ao criar restaurante');
      }
      return null;
    }
  };

  const linkOwnerToRestaurant = async (restaurantId: string, userId: string) => {
    try {
      // Verificar se já existe vínculo
      const { data: existing } = await supabase
        .from('restaurant_users')
        .select('id')
        .eq('restaurant_id', restaurantId)
        .eq('user_id', userId)
        .maybeSingle();

      if (existing) {
        // Atualizar para owner
        const { error } = await supabase
          .from('restaurant_users')
          .update({ role: 'restaurant_owner' })
          .eq('id', existing.id);

        if (error) throw error;
      } else {
        // Criar novo vínculo
        const { error } = await supabase
          .from('restaurant_users')
          .insert({
            restaurant_id: restaurantId,
            user_id: userId,
            role: 'restaurant_owner',
          });

        if (error) throw error;
      }

      toast.success('Proprietário vinculado com sucesso');
      await fetchRestaurants();
      return true;
    } catch (error) {
      console.error('Error linking owner:', error);
      toast.error('Erro ao vincular proprietário');
      return false;
    }
  };

  const fetchAvailableUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name, phone')
        .order('full_name');

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching users:', error);
      return [];
    }
  };

  useEffect(() => {
    const fetchAll = async () => {
      setIsLoading(true);
      await Promise.all([fetchRestaurants(), fetchPayments()]);
      setIsLoading(false);
    };
    fetchAll();
  }, [fetchRestaurants, fetchPayments]);

  const updateRestaurant = async (
    restaurantId: string,
    data: { name?: string; slug?: string; phone?: string; address?: string }
  ) => {
    try {
      const { error } = await supabase
        .from('restaurants')
        .update({
          name: data.name,
          slug: data.slug,
          phone: data.phone || null,
          address: data.address || null,
        })
        .eq('id', restaurantId);

      if (error) throw error;

      toast.success('Restaurante atualizado com sucesso');
      fetchRestaurants();
      return true;
    } catch (error: any) {
      console.error('Error updating restaurant:', error);
      if (error.code === '23505') {
        toast.error('Slug já existe. Escolha outro identificador.');
      } else {
        toast.error('Erro ao atualizar restaurante');
      }
      return false;
    }
  };

  return {
    restaurants,
    payments,
    stats,
    isLoading,
    fetchRestaurants,
    fetchPayments,
    suspendRestaurant,
    activateRestaurant,
    createSubscription,
    registerPayment,
    createRestaurant,
    linkOwnerToRestaurant,
    fetchAvailableUsers,
    updateRestaurant,
  };
}
